import type { Locale } from './locales'
import { DEFAULT_LOCALE } from './locales'

// JSONB shape stored in DB: { "pt-BR": "...", "en-US": "...", "es": "..." }
// Also accepts plain string for tolerance with legacy/unmigrated data.
export type I18nText = Partial<Record<Locale, string>> | string | null | undefined

/**
 * Resolves a localized string with fallback to the default locale (pt-BR).
 * Handles legacy plain strings, null/undefined, and the JSONB shape.
 */
export function resolveI18n(value: I18nText, locale: Locale): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  const localized = value[locale]
  if (localized && localized.trim()) return localized
  const fallback = value[DEFAULT_LOCALE]
  return fallback ?? ''
}

/** Returns true when the value has a non-empty entry for the given locale. */
export function hasI18nEntry(value: I18nText, locale: Locale): boolean {
  if (value == null) return false
  if (typeof value === 'string') return locale === DEFAULT_LOCALE && value.trim().length > 0
  const entry = value[locale]
  return !!entry && entry.trim().length > 0
}

/** Builds an I18nText from a plain PT-BR string (for forms + migrations). */
export function i18nFromString(value: string | null | undefined): I18nText {
  if (!value) return null
  return { [DEFAULT_LOCALE]: value }
}

/** Normalizes an I18nText — drops empty strings, returns null if nothing remains. */
export function normalizeI18n(value: I18nText): Partial<Record<Locale, string>> | null {
  if (value == null) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? { [DEFAULT_LOCALE]: trimmed } : null
  }
  const out: Partial<Record<Locale, string>> = {}
  for (const [k, v] of Object.entries(value) as [Locale, string | undefined][]) {
    if (v && v.trim()) out[k] = v
  }
  return Object.keys(out).length > 0 ? out : null
}
